import React, { useEffect, useState } from 'react';
import { Alert } from '@fx-ui/fine-design';
import { ECode, EMPTY_FN, get, isEmpty, redirect, sessionStorageManager } from '@libs/shared';
import { VALID_STEP, VALID_TYPE } from '../../interfaces';
import DialogForMobile from './components/alert-for-mobile';
import { bindPhone, checkCaptcha, checkPassword, handleMfa } from '../../apis';
import VerifyChangePane from '../verify-change-pane';
import { platformIntl, SESSION_STORAGE, SITE_ROUTER } from '../../constants';
import { rsaEncrypt, getSiteUrl } from '../../utils';
import { globalState } from '../../models';

interface IAuthMfa {
    // 是否显示二次验证/绑定手机号弹窗
    visible: boolean;
    // 用于二次验证的手机号，若没有则走绑定逻辑
    phone?: string;
    // 手机绑定前身份校验时显示的邮箱
    email?: string;
    // 二次验证成功回调
    onMfaSuccess?: () => void;
    // 二次验证失败回调
    onMfaFailed?: () => void;
    // 弹窗关闭回调
    onClose?: () => void;
    // 支持的身份安全校验方式
    validTypeList?: VALID_TYPE[];
}

enum BIND_STEP {
    // 弹窗提示企业开启了二次验证
    STEP_ALERT,
    // 身份安全校验
    STEP_SAFE_VERIFY,
    // 手机验证码校验
    STEP_PHONE_VERIFY
}

/**
 * 二次验证(mfa)组件
 * 有手机号时走手机验证逻辑，无手机号则走绑定手机号逻辑
 */
const AuthMfa: React.FC<IAuthMfa> = (props) => {
    const {
        visible,
        phone,
        email,
        validTypeList = [],
        onMfaSuccess = EMPTY_FN,
        onMfaFailed = EMPTY_FN,
        onClose = EMPTY_FN
    } = props;
    const [validType, setValidType] = useState<VALID_TYPE>(VALID_TYPE.PHONE); // 校验类型（内部采用什么校验组件邮箱校验、密码校验等)
    const [codeValid, setCodeValid] = useState<boolean>(true); // 验证码状态
    const [bindStep, setBindStep] = useState<BIND_STEP>(BIND_STEP.STEP_ALERT); // 绑定步骤：弹窗=>身份安全验证=>手机绑定

    useEffect(() => {
        if (visible) {
            setBindStep(BIND_STEP.STEP_ALERT);
        }
    }, [visible]);

    useEffect(() => {
        // 身份安全校验时，默认用支持的第一种验证方式
        if (bindStep === BIND_STEP.STEP_SAFE_VERIFY) {
            setValidType(validTypeList[0]!);
        } else {
            setValidType(VALID_TYPE.PHONE);
        }
    }, [bindStep]);

    if (!visible) {
        return null;
    }

    /**
     * 二次验证请求发起
     *
     * @param code 验证码
     */
    const handleMfaLogin = (code: string) => {
        return new Promise<void>((resolve) => {
            handleMfa(code)
                .then(() => {
                    onMfaSuccess();
                })
                .catch((error: any) => {
                    onMfaFailed();
                    const errCode = get(error, 'response.data.code');
                    if (ECode.USER_PHONE_AUTH_CODE_INVALID === errCode) {
                        setCodeValid(false);
                    }
                    if (ECode.USER_IS_CANCELING === errCode) {
                        sessionStorageManager.set(
                            SESSION_STORAGE.DELETED_ACCOUNT_TOKEN,
                            get(error, 'response.data.meta.token', '')
                        );
                        redirect(getSiteUrl(SITE_ROUTER.ACCOUNT.CANCEL_VIEW));
                    }
                })
                .finally(() => {
                    resolve();
                });
        });
    };

    /**
     * 处理身份安全验证
     *
     * @param _phone 手机号
     * @param value 验证组件输入的值
     */
    const handleSafeVerify = (_phone: string, value: string) => {
        if (validType === VALID_TYPE.EMAIL) {
            checkCaptcha({
                email,
                email_code: value,
                type: 'email'
            })
                .then(() => {
                    setBindStep(BIND_STEP.STEP_PHONE_VERIFY);
                })
                .catch(() => {
                    setCodeValid(false);
                });
        }
        if (validType === VALID_TYPE.PWD) {
            checkPassword({
                confirmPassword: rsaEncrypt(value)
            })
                .then(() => {
                    setBindStep(BIND_STEP.STEP_PHONE_VERIFY);
                })
                .catch(() => {
                    setCodeValid(false);
                });
        }
    };

    /**
     * 处理手机号绑定验证
     *
     * @param phone 手机号
     * @param code 验证码
     */
    const handlePhoneVerify = (phone: string, code: string) => {
        bindPhone({
            phone,
            phone_code: code
        })
            .then(() => {
                onMfaSuccess();
            })
            .catch((error: any) => {
                onMfaFailed();
                const errCode = get(error, 'response.data.code');
                if (errCode === ECode.USER_PHONE_AUTH_CODE_INVALID) {
                    setCodeValid(false);
                }
                if (errCode === ECode.USER_IS_CANCELING) {
                    sessionStorageManager.set(
                        SESSION_STORAGE.DELETED_ACCOUNT_TOKEN,
                        get(error, 'response.data.meta.token', '')
                    );
                    redirect(getSiteUrl(SITE_ROUTER.ACCOUNT.CANCEL_VIEW));
                }
            });
    };

    /**
     * 渲染手机号绑定第一步：弹窗提示
     */
    const renderAlert = () => {
        const handleCancelClick = () => {
            onClose();
        };
        const handleOkClick = () => {
            if (isEmpty(validTypeList)) {
                // 没有任何验证方式时, 不进行验证
                setBindStep(BIND_STEP.STEP_PHONE_VERIFY);
            } else {
                // 有验证方式则进行身份安全验证
                setBindStep(BIND_STEP.STEP_SAFE_VERIFY);
            }
        };
        if (globalState.isMobile) {
            return (
                <DialogForMobile
                    visible
                    text4Cancel={platformIntl.get('cancel')}
                    text4Ok={platformIntl.get('ok')}
                    title={platformIntl.get('phone.bind')}
                    message={platformIntl.get('corp.mfa.tip')}
                    onCancel={handleCancelClick}
                    onOk={handleOkClick}
                />
            );
        }
        return (
            <Alert
                type="warning"
                visible
                text4Cancel={platformIntl.get('cancel')}
                text4Ok={platformIntl.get('ok')}
                title={platformIntl.get('phone.bind')}
                message={platformIntl.get('corp.mfa.tip')}
                onCancel={handleCancelClick}
                onOk={handleOkClick}
            />
        );
    };

    /**
     * 渲染手机号绑定第二步：身份安全校验
     */
    const renderSafeVerify = () => {
        return (
            <VerifyChangePane
                mode={VALID_STEP.BIND}
                validType={validType}
                setValidType={setValidType}
                availableValidateType={validTypeList}
                userInfo={{ email }}
                codeValid={codeValid}
                setCodeValid={setCodeValid}
                visible={visible}
                onClose={onClose}
                onModifySuccess={handleSafeVerify}
            />
        );
    };

    /**
     * 渲染手机号绑定第三步：手机号验证
     */
    const renderPhoneVerify = () => {
        return (
            <VerifyChangePane
                mode={VALID_STEP.BIND2}
                validType={validType}
                setValidType={setValidType}
                availableValidateType={[VALID_TYPE.PHONE]}
                userInfo={{ phone }}
                codeValid={codeValid}
                setCodeValid={setCodeValid}
                visible={visible}
                onClose={onClose}
                onModifySuccess={handlePhoneVerify}
            />
        );
    };

    if (phone) {
        return (
            <VerifyChangePane
                mode={VALID_STEP.MFA}
                validType={validType}
                setValidType={setValidType}
                availableValidateType={[VALID_TYPE.PHONE]}
                userInfo={{ phone }}
                codeValid={codeValid}
                setCodeValid={setCodeValid}
                visible={visible}
                onClose={onClose}
                onModifySuccess={(_phoneNumber: string, code: string) => {
                    handleMfaLogin(code);
                }}
            />
        );
    }

    switch (bindStep) {
        case BIND_STEP.STEP_ALERT:
            return renderAlert();
        case BIND_STEP.STEP_SAFE_VERIFY:
            return renderSafeVerify();
        case BIND_STEP.STEP_PHONE_VERIFY:
            return renderPhoneVerify();
        default:
            return null;
    }
};

export default AuthMfa;
